import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { setDefaultOptions } from 'date-fns';

import { getDateLocale } from '@/utils/date';

// Geo-based language detection from Cloudflare CF-IPCountry header (via nginx cookie)
const detectLanguageFromCountry = (): string | null => {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match(/geo_country=([A-Z]{2})/);
  const country = match?.[1];

  if (!country) return null;

  // Brazil → Brazilian Portuguese
  if (country === 'BR') return 'pt-BR';

  // Portugal and other Portuguese-speaking countries → European Portuguese
  // PT (Portugal), AO (Angola), MZ (Mozambique), CV (Cape Verde),
  // GW (Guinea-Bissau), ST (São Tomé), TL (Timor-Leste)
  if (['PT', 'AO', 'MZ', 'CV', 'GW', 'ST', 'TL'].includes(country)) return 'pt-PT';

  // DE, AT, CH → German
  if (['DE', 'AT', 'CH'].includes(country)) return 'de';

  // Poland → Polish
  if (country === 'PL') return 'pl';

  // All others → English
  return 'en';
};

// Custom geo detector for i18next
const geoDetector = {
  name: 'geoDetector',
  lookup(): string | undefined {
    const lang = detectLanguageFromCountry();
    return lang || undefined;
  },
};

// Import all translation files
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enBooking from './locales/en/booking.json';
import enBookings from './locales/en/bookings.json';
import enPublicBooking from './locales/en/publicBooking.json';
import enNewBooking from './locales/en/newBooking.json';
import enAdmin from './locales/en/admin.json';
import enServices from './locales/en/services.json';
import enProviders from './locales/en/providers.json';
import enValidation from './locales/en/validation.json';
import enOnboarding from './locales/en/onboarding.json';
import enHeader from './locales/en/header.json';
import enDashboard from './locales/en/dashboard.json';
import enLogin from './locales/en/login.json';
import enRegistration from './locales/en/registration.json';
import enSettings from './locales/en/settings.json';
import enTimeSlots from './locales/en/timeSlots.json';
import enHome from './locales/en/home.json';
import enProfile from './locales/en/profile.json';
import enInvitations from './locales/en/invitations.json';
import enTeam from './locales/en/team.json';
import enStaff from './locales/en/staff.json';
import enCalendar from './locales/en/calendar.json';
import enLandingCommon from './locales/en/landing-common.json';
import enLandingSalons from './locales/en/landing-salons.json';
import enLandingDoctors from './locales/en/landing-doctors.json';
import enLandingGolfTeachers from './locales/en/landing-golf-teachers.json';
import enLandingBeauty from './locales/en/landing-beauty.json';
import enLandingSpa from './locales/en/landing-spa.json';
import enLandingTrainers from './locales/en/landing-trainers.json';
import enLandingCoaches from './locales/en/landing-coaches.json';
import enLandingBarbers from './locales/en/landing-barbers.json';
import enLandingClinics from './locales/en/landing-clinics.json';
import enSessions from './locales/en/sessions.json';
import enLandingTherapistsInvite from './locales/en/landing-therapists-invite.json';
import enLandingSmallGyms from './locales/en/landing-small-gyms.json';
import enFeatures from './locales/en/features.json';
import enCompare from './locales/en/compare.json';
import enSubscription from './locales/en/subscription.json';
import enBilling from './locales/en/billing.json';
import enFooter from './locales/en/footer.json';
import enLegal from './locales/en/legal.json';
import enPlans from './locales/en/plans.json';
import enTips from './locales/en/tips.json';
import enTour from './locales/en/tour.json';
import enGuestBooking from './locales/en/guestBooking.json';
import enAbout from './locales/en/about.json';
import enBlog from './locales/en/blog.json';
import enCustomers from './locales/en/customers.json';
import enWaitlist from './locales/en/waitlist.json';
import enMyBookings from './locales/en/myBookings.json';
import enBookingPage from './locales/en/bookingPage.json';
import enAdminBookingPage from './locales/en/adminBookingPage.json';

import ptBRCommon from './locales/pt-BR/common.json';
import ptBRAuth from './locales/pt-BR/auth.json';
import ptBRBooking from './locales/pt-BR/booking.json';
import ptBRBookings from './locales/pt-BR/bookings.json';
import ptBRPublicBooking from './locales/pt-BR/publicBooking.json';
import ptBRNewBooking from './locales/pt-BR/newBooking.json';
import ptBRAdmin from './locales/pt-BR/admin.json';
import ptBRServices from './locales/pt-BR/services.json';
import ptBRProviders from './locales/pt-BR/providers.json';
import ptBRValidation from './locales/pt-BR/validation.json';
import ptBROnboarding from './locales/pt-BR/onboarding.json';
import ptBRHeader from './locales/pt-BR/header.json';
import ptBRDashboard from './locales/pt-BR/dashboard.json';
import ptBRLogin from './locales/pt-BR/login.json';
import ptBRRegistration from './locales/pt-BR/registration.json';
import ptBRSettings from './locales/pt-BR/settings.json';
import ptBRTimeSlots from './locales/pt-BR/timeSlots.json';
import ptBRHome from './locales/pt-BR/home.json';
import ptBRProfile from './locales/pt-BR/profile.json';
import ptBRInvitations from './locales/pt-BR/invitations.json';
import ptBRTeam from './locales/pt-BR/team.json';
import ptBRStaff from './locales/pt-BR/staff.json';
import ptBRCalendar from './locales/pt-BR/calendar.json';
import ptBRLandingCommon from './locales/pt-BR/landing-common.json';
import ptBRLandingSalons from './locales/pt-BR/landing-salons.json';
import ptBRLandingDoctors from './locales/pt-BR/landing-doctors.json';
import ptBRLandingGolfTeachers from './locales/pt-BR/landing-golf-teachers.json';
import ptBRLandingBeauty from './locales/pt-BR/landing-beauty.json';
import ptBRLandingSpa from './locales/pt-BR/landing-spa.json';
import ptBRLandingTrainers from './locales/pt-BR/landing-trainers.json';
import ptBRLandingCoaches from './locales/pt-BR/landing-coaches.json';
import ptBRLandingBarbers from './locales/pt-BR/landing-barbers.json';
import ptBRLandingClinics from './locales/pt-BR/landing-clinics.json';
import ptBRSessions from './locales/pt-BR/sessions.json';
import ptBRLandingTherapistsInvite from './locales/pt-BR/landing-therapists-invite.json';
import ptBRLandingSmallGyms from './locales/pt-BR/landing-small-gyms.json';
import ptBRFeatures from './locales/pt-BR/features.json';
import ptBRCompare from './locales/pt-BR/compare.json';
import ptBRSubscription from './locales/pt-BR/subscription.json';
import ptBRBilling from './locales/pt-BR/billing.json';
import ptBRFooter from './locales/pt-BR/footer.json';
import ptBRLegal from './locales/pt-BR/legal.json';
import ptBRPlans from './locales/pt-BR/plans.json';
import ptBRTips from './locales/pt-BR/tips.json';
import ptBRTour from './locales/pt-BR/tour.json';
import ptBRGuestBooking from './locales/pt-BR/guestBooking.json';
import ptBRAbout from './locales/pt-BR/about.json';
import ptBRBlog from './locales/pt-BR/blog.json';
import ptBRCustomers from './locales/pt-BR/customers.json';
import ptBRWaitlist from './locales/pt-BR/waitlist.json';
import ptBRMyBookings from './locales/pt-BR/myBookings.json';
import ptBRBookingPage from './locales/pt-BR/bookingPage.json';
import ptBRAdminBookingPage from './locales/pt-BR/adminBookingPage.json';

import ptPTCommon from './locales/pt-PT/common.json';
import ptPTAuth from './locales/pt-PT/auth.json';
import ptPTBooking from './locales/pt-PT/booking.json';
import ptPTBookings from './locales/pt-PT/bookings.json';
import ptPTPublicBooking from './locales/pt-PT/publicBooking.json';
import ptPTNewBooking from './locales/pt-PT/newBooking.json';
import ptPTAdmin from './locales/pt-PT/admin.json';
import ptPTServices from './locales/pt-PT/services.json';
import ptPTProviders from './locales/pt-PT/providers.json';
import ptPTValidation from './locales/pt-PT/validation.json';
import ptPTOnboarding from './locales/pt-PT/onboarding.json';
import ptPTHeader from './locales/pt-PT/header.json';
import ptPTDashboard from './locales/pt-PT/dashboard.json';
import ptPTLogin from './locales/pt-PT/login.json';
import ptPTRegistration from './locales/pt-PT/registration.json';
import ptPTSettings from './locales/pt-PT/settings.json';
import ptPTTimeSlots from './locales/pt-PT/timeSlots.json';
import ptPTHome from './locales/pt-PT/home.json';
import ptPTProfile from './locales/pt-PT/profile.json';
import ptPTInvitations from './locales/pt-PT/invitations.json';
import ptPTTeam from './locales/pt-PT/team.json';
import ptPTStaff from './locales/pt-PT/staff.json';
import ptPTCalendar from './locales/pt-PT/calendar.json';
import ptPTLandingCommon from './locales/pt-PT/landing-common.json';
import ptPTLandingSalons from './locales/pt-PT/landing-salons.json';
import ptPTLandingDoctors from './locales/pt-PT/landing-doctors.json';
import ptPTLandingGolfTeachers from './locales/pt-PT/landing-golf-teachers.json';
import ptPTLandingBeauty from './locales/pt-PT/landing-beauty.json';
import ptPTLandingSpa from './locales/pt-PT/landing-spa.json';
import ptPTLandingTrainers from './locales/pt-PT/landing-trainers.json';
import ptPTLandingCoaches from './locales/pt-PT/landing-coaches.json';
import ptPTLandingBarbers from './locales/pt-PT/landing-barbers.json';
import ptPTLandingClinics from './locales/pt-PT/landing-clinics.json';
import ptPTSessions from './locales/pt-PT/sessions.json';
import ptPTLandingTherapistsInvite from './locales/pt-PT/landing-therapists-invite.json';
import ptPTLandingSmallGyms from './locales/pt-PT/landing-small-gyms.json';
import ptPTFeatures from './locales/pt-PT/features.json';
import ptPTCompare from './locales/pt-PT/compare.json';
import ptPTSubscription from './locales/pt-PT/subscription.json';
import ptPTBilling from './locales/pt-PT/billing.json';
import ptPTFooter from './locales/pt-PT/footer.json';
import ptPTLegal from './locales/pt-PT/legal.json';
import ptPTPlans from './locales/pt-PT/plans.json';
import ptPTTips from './locales/pt-PT/tips.json';
import ptPTTour from './locales/pt-PT/tour.json';
import ptPTGuestBooking from './locales/pt-PT/guestBooking.json';
import ptPTAbout from './locales/pt-PT/about.json';
import ptPTBlog from './locales/pt-PT/blog.json';
import ptPTCustomers from './locales/pt-PT/customers.json';
import ptPTWaitlist from './locales/pt-PT/waitlist.json';
import ptPTMyBookings from './locales/pt-PT/myBookings.json';
import ptPTBookingPage from './locales/pt-PT/bookingPage.json';
import ptPTAdminBookingPage from './locales/pt-PT/adminBookingPage.json';

import deCommon from './locales/de/common.json';
import deAuth from './locales/de/auth.json';
import deBooking from './locales/de/booking.json';
import deBookings from './locales/de/bookings.json';
import dePublicBooking from './locales/de/publicBooking.json';
import deNewBooking from './locales/de/newBooking.json';
import deAdmin from './locales/de/admin.json';
import deServices from './locales/de/services.json';
import deProviders from './locales/de/providers.json';
import deValidation from './locales/de/validation.json';
import deOnboarding from './locales/de/onboarding.json';
import deHeader from './locales/de/header.json';
import deDashboard from './locales/de/dashboard.json';
import deLogin from './locales/de/login.json';
import deRegistration from './locales/de/registration.json';
import deSettings from './locales/de/settings.json';
import deTimeSlots from './locales/de/timeSlots.json';
import deHome from './locales/de/home.json';
import deProfile from './locales/de/profile.json';
import deInvitations from './locales/de/invitations.json';
import deTeam from './locales/de/team.json';
import deStaff from './locales/de/staff.json';
import deCalendar from './locales/de/calendar.json';
import deLandingCommon from './locales/de/landing-common.json';
import deLandingSalons from './locales/de/landing-salons.json';
import deLandingDoctors from './locales/de/landing-doctors.json';
import deLandingGolfTeachers from './locales/de/landing-golf-teachers.json';
import deLandingBeauty from './locales/de/landing-beauty.json';
import deLandingSpa from './locales/de/landing-spa.json';
import deLandingTrainers from './locales/de/landing-trainers.json';
import deLandingCoaches from './locales/de/landing-coaches.json';
import deLandingBarbers from './locales/de/landing-barbers.json';
import deLandingClinics from './locales/de/landing-clinics.json';
import deSessions from './locales/de/sessions.json';
import deLandingTherapistsInvite from './locales/de/landing-therapists-invite.json';
import deLandingSmallGyms from './locales/de/landing-small-gyms.json';
import deFeatures from './locales/de/features.json';
import deCompare from './locales/de/compare.json';
import deSubscription from './locales/de/subscription.json';
import deBilling from './locales/de/billing.json';
import deFooter from './locales/de/footer.json';
import deLegal from './locales/de/legal.json';
import dePlans from './locales/de/plans.json';
import deTips from './locales/de/tips.json';
import deTour from './locales/de/tour.json';
import deGuestBooking from './locales/de/guestBooking.json';
import deAbout from './locales/de/about.json';
import deBlog from './locales/de/blog.json';
import deCustomers from './locales/de/customers.json';
import deWaitlist from './locales/de/waitlist.json';
import deMyBookings from './locales/de/myBookings.json';
import deBookingPage from './locales/de/bookingPage.json';
import deAdminBookingPage from './locales/de/adminBookingPage.json';

import plCommon from './locales/pl/common.json';
import plAuth from './locales/pl/auth.json';
import plBooking from './locales/pl/booking.json';
import plBookings from './locales/pl/bookings.json';
import plPublicBooking from './locales/pl/publicBooking.json';
import plWaitlist from './locales/pl/waitlist.json';
import plNewBooking from './locales/pl/newBooking.json';
import plAdmin from './locales/pl/admin.json';
import plServices from './locales/pl/services.json';
import plProviders from './locales/pl/providers.json';
import plValidation from './locales/pl/validation.json';
import plOnboarding from './locales/pl/onboarding.json';
import plHeader from './locales/pl/header.json';
import plDashboard from './locales/pl/dashboard.json';
import plLogin from './locales/pl/login.json';
import plRegistration from './locales/pl/registration.json';
import plSettings from './locales/pl/settings.json';
import plTimeSlots from './locales/pl/timeSlots.json';
import plHome from './locales/pl/home.json';
import plProfile from './locales/pl/profile.json';
import plInvitations from './locales/pl/invitations.json';
import plTeam from './locales/pl/team.json';
import plStaff from './locales/pl/staff.json';
import plCalendar from './locales/pl/calendar.json';
import plLandingCommon from './locales/pl/landing-common.json';
import plLandingSalons from './locales/pl/landing-salons.json';
import plLandingDoctors from './locales/pl/landing-doctors.json';
import plLandingGolfTeachers from './locales/pl/landing-golf-teachers.json';
import plLandingBeauty from './locales/pl/landing-beauty.json';
import plLandingSpa from './locales/pl/landing-spa.json';
import plLandingTrainers from './locales/pl/landing-trainers.json';
import plLandingCoaches from './locales/pl/landing-coaches.json';
import plLandingBarbers from './locales/pl/landing-barbers.json';
import plLandingClinics from './locales/pl/landing-clinics.json';
import plSessions from './locales/pl/sessions.json';
import plLandingTherapistsInvite from './locales/pl/landing-therapists-invite.json';
import plLandingSmallGyms from './locales/pl/landing-small-gyms.json';
import plFeatures from './locales/pl/features.json';
import plCompare from './locales/pl/compare.json';
import plSubscription from './locales/pl/subscription.json';
import plBilling from './locales/pl/billing.json';
import plFooter from './locales/pl/footer.json';
import plLegal from './locales/pl/legal.json';
import plPlans from './locales/pl/plans.json';
import plTips from './locales/pl/tips.json';
import plTour from './locales/pl/tour.json';
import plGuestBooking from './locales/pl/guestBooking.json';
import plAbout from './locales/pl/about.json';
import plBlog from './locales/pl/blog.json';
import plCustomers from './locales/pl/customers.json';
import plMyBookings from './locales/pl/myBookings.json';
import plBookingPage from './locales/pl/bookingPage.json';
import plAdminBookingPage from './locales/pl/adminBookingPage.json';

// Define resources
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    booking: enBooking,
    bookings: enBookings,
    publicBooking: enPublicBooking,
    newBooking: enNewBooking,
    admin: enAdmin,
    services: enServices,
    providers: enProviders,
    validation: enValidation,
    onboarding: enOnboarding,
    header: enHeader,
    dashboard: enDashboard,
    login: enLogin,
    registration: enRegistration,
    settings: enSettings,
    timeSlots: enTimeSlots,
    home: enHome,
    profile: enProfile,
    invitations: enInvitations,
    team: enTeam,
    staff: enStaff,
    calendar: enCalendar,
    'landing-common': enLandingCommon,
    'landing-salons': enLandingSalons,
    'landing-doctors': enLandingDoctors,
    'landing-golf-teachers': enLandingGolfTeachers,
    'landing-beauty': enLandingBeauty,
    'landing-spa': enLandingSpa,
    'landing-trainers': enLandingTrainers,
    'landing-coaches': enLandingCoaches,
    'landing-barbers': enLandingBarbers,
    'landing-clinics': enLandingClinics,
    sessions: enSessions,
    'landing-therapists-invite': enLandingTherapistsInvite,
    'landing-small-gyms': enLandingSmallGyms,
    features: enFeatures,
    compare: enCompare,
    subscription: enSubscription,
    billing: enBilling,
    footer: enFooter,
    legal: enLegal,
    plans: enPlans,
    tips: enTips,
    tour: enTour,
    guestBooking: enGuestBooking,
    about: enAbout,
    blog: enBlog,
    customers: enCustomers,
    waitlist: enWaitlist,
    myBookings: enMyBookings,
    bookingPage: enBookingPage,
    adminBookingPage: enAdminBookingPage,
  },
  'pt-BR': {
    common: ptBRCommon,
    auth: ptBRAuth,
    booking: ptBRBooking,
    bookings: ptBRBookings,
    publicBooking: ptBRPublicBooking,
    newBooking: ptBRNewBooking,
    admin: ptBRAdmin,
    services: ptBRServices,
    providers: ptBRProviders,
    validation: ptBRValidation,
    onboarding: ptBROnboarding,
    header: ptBRHeader,
    dashboard: ptBRDashboard,
    login: ptBRLogin,
    registration: ptBRRegistration,
    settings: ptBRSettings,
    timeSlots: ptBRTimeSlots,
    home: ptBRHome,
    profile: ptBRProfile,
    invitations: ptBRInvitations,
    team: ptBRTeam,
    staff: ptBRStaff,
    calendar: ptBRCalendar,
    'landing-common': ptBRLandingCommon,
    'landing-salons': ptBRLandingSalons,
    'landing-doctors': ptBRLandingDoctors,
    'landing-golf-teachers': ptBRLandingGolfTeachers,
    'landing-beauty': ptBRLandingBeauty,
    'landing-spa': ptBRLandingSpa,
    'landing-trainers': ptBRLandingTrainers,
    'landing-coaches': ptBRLandingCoaches,
    'landing-barbers': ptBRLandingBarbers,
    'landing-clinics': ptBRLandingClinics,
    sessions: ptBRSessions,
    'landing-therapists-invite': ptBRLandingTherapistsInvite,
    'landing-small-gyms': ptBRLandingSmallGyms,
    features: ptBRFeatures,
    compare: ptBRCompare,
    subscription: ptBRSubscription,
    billing: ptBRBilling,
    footer: ptBRFooter,
    legal: ptBRLegal,
    plans: ptBRPlans,
    tips: ptBRTips,
    tour: ptBRTour,
    guestBooking: ptBRGuestBooking,
    about: ptBRAbout,
    blog: ptBRBlog,
    customers: ptBRCustomers,
    waitlist: ptBRWaitlist,
    myBookings: ptBRMyBookings,
    bookingPage: ptBRBookingPage,
    adminBookingPage: ptBRAdminBookingPage,
  },
  'pt-PT': {
    common: ptPTCommon,
    auth: ptPTAuth,
    booking: ptPTBooking,
    bookings: ptPTBookings,
    publicBooking: ptPTPublicBooking,
    newBooking: ptPTNewBooking,
    admin: ptPTAdmin,
    services: ptPTServices,
    providers: ptPTProviders,
    validation: ptPTValidation,
    onboarding: ptPTOnboarding,
    header: ptPTHeader,
    dashboard: ptPTDashboard,
    login: ptPTLogin,
    registration: ptPTRegistration,
    settings: ptPTSettings,
    timeSlots: ptPTTimeSlots,
    home: ptPTHome,
    profile: ptPTProfile,
    invitations: ptPTInvitations,
    team: ptPTTeam,
    staff: ptPTStaff,
    calendar: ptPTCalendar,
    'landing-common': ptPTLandingCommon,
    'landing-salons': ptPTLandingSalons,
    'landing-doctors': ptPTLandingDoctors,
    'landing-golf-teachers': ptPTLandingGolfTeachers,
    'landing-beauty': ptPTLandingBeauty,
    'landing-spa': ptPTLandingSpa,
    'landing-trainers': ptPTLandingTrainers,
    'landing-coaches': ptPTLandingCoaches,
    'landing-barbers': ptPTLandingBarbers,
    'landing-clinics': ptPTLandingClinics,
    sessions: ptPTSessions,
    'landing-therapists-invite': ptPTLandingTherapistsInvite,
    'landing-small-gyms': ptPTLandingSmallGyms,
    features: ptPTFeatures,
    compare: ptPTCompare,
    subscription: ptPTSubscription,
    billing: ptPTBilling,
    footer: ptPTFooter,
    legal: ptPTLegal,
    plans: ptPTPlans,
    tips: ptPTTips,
    tour: ptPTTour,
    guestBooking: ptPTGuestBooking,
    about: ptPTAbout,
    blog: ptPTBlog,
    customers: ptPTCustomers,
    waitlist: ptPTWaitlist,
    myBookings: ptPTMyBookings,
    bookingPage: ptPTBookingPage,
    adminBookingPage: ptPTAdminBookingPage,
  },
  de: {
    common: deCommon,
    auth: deAuth,
    booking: deBooking,
    bookings: deBookings,
    publicBooking: dePublicBooking,
    newBooking: deNewBooking,
    admin: deAdmin,
    services: deServices,
    providers: deProviders,
    validation: deValidation,
    onboarding: deOnboarding,
    header: deHeader,
    dashboard: deDashboard,
    login: deLogin,
    registration: deRegistration,
    settings: deSettings,
    timeSlots: deTimeSlots,
    home: deHome,
    profile: deProfile,
    invitations: deInvitations,
    team: deTeam,
    staff: deStaff,
    calendar: deCalendar,
    'landing-common': deLandingCommon,
    'landing-salons': deLandingSalons,
    'landing-doctors': deLandingDoctors,
    'landing-golf-teachers': deLandingGolfTeachers,
    'landing-beauty': deLandingBeauty,
    'landing-spa': deLandingSpa,
    'landing-trainers': deLandingTrainers,
    'landing-coaches': deLandingCoaches,
    'landing-barbers': deLandingBarbers,
    'landing-clinics': deLandingClinics,
    sessions: deSessions,
    'landing-therapists-invite': deLandingTherapistsInvite,
    'landing-small-gyms': deLandingSmallGyms,
    features: deFeatures,
    compare: deCompare,
    subscription: deSubscription,
    billing: deBilling,
    footer: deFooter,
    legal: deLegal,
    plans: dePlans,
    tips: deTips,
    tour: deTour,
    guestBooking: deGuestBooking,
    about: deAbout,
    blog: deBlog,
    customers: deCustomers,
    waitlist: deWaitlist,
    myBookings: deMyBookings,
    bookingPage: deBookingPage,
    adminBookingPage: deAdminBookingPage,
  },
  pl: {
    common: plCommon,
    auth: plAuth,
    booking: plBooking,
    bookings: plBookings,
    publicBooking: plPublicBooking,
    waitlist: plWaitlist,
    newBooking: plNewBooking,
    admin: plAdmin,
    services: plServices,
    providers: plProviders,
    validation: plValidation,
    onboarding: plOnboarding,
    header: plHeader,
    dashboard: plDashboard,
    login: plLogin,
    registration: plRegistration,
    settings: plSettings,
    timeSlots: plTimeSlots,
    home: plHome,
    profile: plProfile,
    invitations: plInvitations,
    team: plTeam,
    staff: plStaff,
    calendar: plCalendar,
    'landing-common': plLandingCommon,
    'landing-salons': plLandingSalons,
    'landing-doctors': plLandingDoctors,
    'landing-golf-teachers': plLandingGolfTeachers,
    'landing-beauty': plLandingBeauty,
    'landing-spa': plLandingSpa,
    'landing-trainers': plLandingTrainers,
    'landing-coaches': plLandingCoaches,
    'landing-barbers': plLandingBarbers,
    'landing-clinics': plLandingClinics,
    sessions: plSessions,
    'landing-therapists-invite': plLandingTherapistsInvite,
    'landing-small-gyms': plLandingSmallGyms,
    features: plFeatures,
    compare: plCompare,
    subscription: plSubscription,
    billing: plBilling,
    footer: plFooter,
    legal: plLegal,
    plans: plPlans,
    tips: plTips,
    tour: plTour,
    guestBooking: plGuestBooking,
    about: plAbout,
    blog: plBlog,
    customers: plCustomers,
    myBookings: plMyBookings,
    bookingPage: plBookingPage,
    adminBookingPage: plAdminBookingPage,
  },
};

// Create language detector instance and add custom geo detector
const languageDetector = new LanguageDetector();
languageDetector.addDetector(geoDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS: 'common',
    fallbackLng: 'en',

    // Language detection order:
    // 1. localStorage - user's manual choice (persists)
    // 2. geoDetector - country from Cloudflare CF-IPCountry header
    // 3. navigator - browser language preference
    // 4. htmlTag - fallback
    detection: {
      order: ['localStorage', 'geoDetector', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'bookinghub_language',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    // React-specific options
    react: {
      useSuspense: true,
    },

    // Show missing keys in development
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation: [${lng}][${ns}][${key}]`);
      }
    },
  });

// Register Polish plural rules (CLDR: one/few/many) - must be after init()
i18n.services.pluralResolver.addRule('pl', {
  numbers: [1, 2, 5, 21, 22, 25],
  plurals: (n: number) => {
    if (n === 1) return 'one';
    if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return 'few';
    return 'many';
  }
});

// Keep date-fns formatting in sync with the active UI language, so weekday /
// day / month names localize without passing a locale to every format() call.
const syncDateFnsLocale = (lng: string) => {
  setDefaultOptions({ locale: getDateLocale(lng) });
};
syncDateFnsLocale(i18n.language || 'en');
i18n.on('languageChanged', syncDateFnsLocale);

export default i18n;
